import React, { useEffect, useState } from "react";
import { Copy, Upload, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatusBadge, fmtUSD, fmtDate } from "@/components/ui-bits";
import { Field } from "@/pages/Login";

export default function Deposit() {
  const [settings, setSettings] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [amount, setAmount] = useState("");
  const [txHash, setTxHash] = useState("");
  const [proofPath, setProofPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    const [s, d] = await Promise.all([api.get("/settings/public"), api.get("/deposits/mine")]);
    setSettings(s.data); setDeposits(d.data);
  };
  useEffect(() => { load(); }, []);

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success("Address copied"); };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/deposits/upload-proof", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProofPath(data.storage_path);
      toast.success("Proof uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    setUploading(false);
  };

  const submit = async () => {
    if (!amount || Number(amount) <= 0) return toast.error("Enter a valid amount");
    if (!txHash.trim()) return toast.error("Enter transaction hash");
    setSubmitting(true);
    try {
      await api.post("/deposits/create", {
        amount: Number(amount), tx_hash: txHash.trim(), screenshot_path: proofPath,
      });
      toast.success("Deposit submitted for review");
      setAmount(""); setTxHash(""); setProofPath("");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    setSubmitting(false);
  };

  const wallet = settings?.usdt_wallet || "";
  const qr = wallet ? `https://api.qrserver.com/v1/create-qr-code/?size=220x220&bgcolor=0A132A&color=00F0FF&data=${encodeURIComponent(wallet)}` : "";

  return (
    <div>
      <PageHeader title="Deposit USDT" subtitle="Send USDT (BEP20) to the address below and submit proof for admin verification." />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="glass-strong p-4 sm:p-6 rounded-xl neon-glow">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#94A3B8] font-bold mb-3">USDT Wallet (BEP20)</div>
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="font-mono text-[11px] sm:text-sm bg-[#060B19] border border-white/10 rounded-lg px-2 sm:px-3 py-2 sm:py-2.5 flex-1 break-all min-w-0" data-testid="usdt-wallet">{wallet || "Loading…"}</div>
            <button onClick={() => copy(wallet)} data-testid="copy-wallet" className="p-2 sm:p-2.5 rounded-lg bg-[#00F0FF] text-[#060B19] hover:bg-[#00D4E0] transition-all flex-shrink-0"><Copy size={16} /></button>
          </div>
          <div className="flex justify-center mt-5 sm:mt-6">
            {qr && <img src={qr} alt="USDT QR" className="rounded-xl border border-white/10 p-2 bg-[#0A132A] max-w-[200px] sm:max-w-[220px] w-full h-auto" data-testid="usdt-qr" />}
          </div>
          <div className="text-[11px] sm:text-xs text-[#94A3B8] mt-4 leading-relaxed">
            <span className="text-[#FFC400]">⚠</span> Only send <strong>USDT (BEP20)</strong> to this address. Transfers from other networks may be lost.
          </div>
        </div>

        <div className="glass p-4 sm:p-6 rounded-xl">
          <h3 className="font-display text-lg sm:text-xl font-bold mb-4">Submit Deposit</h3>
          <div className="space-y-4">
            <Field label="Deposit Amount (USDT)" type="number" value={amount} onChange={setAmount} testid="deposit-amount" />
            <Field label="Transaction Hash" value={txHash} onChange={setTxHash} testid="deposit-txhash" placeholder="0x..." />
            <div>
              <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-bold">Upload Screenshot</span>
              <label className="mt-2 flex items-center justify-center w-full h-28 border-2 border-dashed border-white/10 rounded-lg cursor-pointer hover:border-[#00F0FF]/40 transition-all bg-[#0A132A]/50">
                {uploading ? (
                  <div className="text-[#00F0FF] text-sm">Uploading…</div>
                ) : proofPath ? (
                  <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 size={18} /> Uploaded — replace?</div>
                ) : (
                  <div className="flex flex-col items-center text-[#94A3B8] text-sm"><Upload size={20} className="mb-1.5" /> Click to upload (JPG/PNG, ≤10MB)</div>
                )}
                <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="deposit-file" />
              </label>
            </div>
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="deposit-submit"
              className="w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_24px_rgba(0,240,255,0.45)] transition-all disabled:opacity-50"
            >
              {submitting ? "Submitting…" : "Submit Deposit"}
            </button>
          </div>
        </div>
      </div>

      <div className="glass p-5 mt-6">
        <h3 className="font-display text-lg font-bold mb-4">Contribution Status</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase tracking-wider text-[#475569]">
              <tr className="border-b border-white/5">
                <th className="text-left py-2">Date</th><th className="text-left">Amount</th>
                <th className="text-left">TX Hash</th><th className="text-right">Status</th>
              </tr>
            </thead>
            <tbody data-testid="deposit-history">
              {deposits.map((d) => (
                <tr key={d.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="py-3 text-[#94A3B8]">{fmtDate(d.created_at)}</td>
                  <td className="font-mono">{fmtUSD(d.amount)}</td>
                  <td className="font-mono text-xs text-[#94A3B8] truncate max-w-[200px]">{d.tx_hash}</td>
                  <td className="text-right"><StatusBadge status={d.status} /></td>
                </tr>
              ))}
              {deposits.length === 0 && <tr><td colSpan={4} className="py-8 text-center text-[#475569]">No deposits yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
